import { useState, useEffect, useContext, useCallback } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router';

import Footer from '../../../shared/components/Globals/Footer/Footer';
import LoadingFullScreen from '../../../shared/components/Globals/LoadingFullScreen/LoadingFullScreen';
import Toast from '../../../shared/components/Globals/Toast/Toast';
import { LOGIN_ROUTE, RECAPTCHA_SITE_KEY } from '../../../shared/constants/auth';
import { CLICK_ACTION, CREATE_ACCOUNT, CREATE_PASSWORD } from '../../../shared/constants/tagManager';
import { GENERIC_ERROR_TOAST } from '../../../shared/constants/toast';
import { AuthContext } from '../../../shared/hooks/useAuth/useAuth';
import Users from '../../../shared/services/api/users/users';
import TagManagerService from '../../../shared/services/gtm/tagManagerService';
import CreatePassword from '../sections/CreatePassword/CreatePassword';
import VerificationCode from '../sections/VerificationCode/VerificationCode';

const CreateAccount = () => {
  const [verificationToken, setVerificationToken] = useState<string>('');
  const [verificationFailed, setVerificationFailed] = useState<boolean>(false);
  const [openToast, setOpenToast] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [loadingVerification, setLoadingVerification] = useState(false);

  const { sessionLogin, errorCode, resetError } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as { email?: string; verificationCode?: string } | null;
  const email = state?.email || '';
  const verificationCode = state?.verificationCode || '';

  const { t } = useTranslation();

  useEffect(() => {
    const submitCode = async (code: string) => {
      setLoadingVerification(true);
      try {
        const response = await Users.verifyEmail(email, code);
        setVerificationToken(response.token);
        setStep(1);
      } catch {
        setOpenToast(true);
        setVerificationFailed(true);
      } finally {
        setLoadingVerification(false);
      }
    };

    if (!email) {
      navigate(LOGIN_ROUTE, { replace: true });
      return;
    }
    if (verificationCode) {
      submitCode(verificationCode);
    }
  }, [email, navigate, verificationCode]);

  const errorToast = openToast || Boolean(errorCode);

  const handleCloseToast = useCallback(() => {
    setOpenToast(false);
    if (errorCode) resetError('');
  }, [errorCode, resetError]);

  const handleSignIn = async (userPassword: string): Promise<void> => {
    TagManagerService.pushEvent(CLICK_ACTION, CREATE_PASSWORD);
    return new Promise<void>((resolve, reject) => {
      globalThis.grecaptcha.ready(async () => {
        try {
          const recaptchaToken = await globalThis.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'submit' });
          await sessionLogin(email, userPassword, recaptchaToken);
          resolve();
        } catch (error) {
          reject(error);
        }
      });
    });
  };

  const handleVerificationCode = async (code: string) => {
    try {
      setLoading(true);
      setVerificationFailed(false);
      const response = await Users.verifyEmail(email, code);
      setVerificationToken(response.token);
      setStep(1);
    } catch {
      setOpenToast(true);
      setVerificationFailed(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async (userPassword: string) => {
    setLoading(true);
    TagManagerService.pushEvent(CLICK_ACTION, CREATE_ACCOUNT);
    try {
      await Users.signUp(email, userPassword, verificationToken);
      await handleSignIn(userPassword);
    } catch {
      setOpenToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeStep = (newStep: number) => {
    if (newStep === -1) {
      return navigate(LOGIN_ROUTE);
    }
    setStep(newStep);
  };

  if (loadingVerification) {
    return (
      <div className="auth__container">
        <LoadingFullScreen show={true} text={''} />
      </div>
    );
  }

  return (
    <>
      <div className="auth__container">
        {step === 0 && (
          <VerificationCode
            codeType="create-account"
            email={email}
            loading={loading}
            hasError={verificationFailed}
            handleNext={handleVerificationCode}
            handleGoBack={handleChangeStep}
          />
        )}
        {step === 1 && (
          <CreatePassword
            onSubmit={handleCreateAccount}
            email={email}
            loading={loading}
            handleGoBack={handleChangeStep}
          />
        )}
        <Toast
          message={t(GENERIC_ERROR_TOAST.message)}
          variant={GENERIC_ERROR_TOAST.variant}
          open={errorToast}
          onClose={handleCloseToast}
        />
      </div>
      <Footer isAuthenticated={false} />
    </>
  );
};

export default CreateAccount;
